import { useState, useEffect, useRef, useCallback } from 'react'
import { animate, stagger } from 'animejs'
import { vision, mission } from '../../../data/vision'
import { themes, objectives } from '../../../data/objectives'
import { strategies } from '../../../data/strategies'
import { tactics, quarters } from '../../../data/tactics'

type LayerId = 'vision' | 'mission' | 'objectives' | 'strategies' | 'tactics'

const LAYERS: { id: LayerId; label: string; color: string; textColor: string }[] = [
  { id: 'vision', label: 'Vision', color: '#253D39', textColor: '#F5F5F0' },
  { id: 'mission', label: 'Mission', color: '#395E58', textColor: '#F5F5F0' },
  { id: 'objectives', label: 'Objectives', color: '#619793', textColor: '#F5F5F0' },
  { id: 'strategies', label: 'Strategies', color: '#9AC7BF', textColor: '#253D39' },
  { id: 'tactics', label: 'Tactics', color: '#F5F5F0', textColor: '#253D39' },
]

function slideDown(el: HTMLElement, duration = 600) {
  el.style.overflow = 'hidden'
  el.style.height = '0px'
  el.style.opacity = '0'
  el.style.display = 'block'
  const targetHeight = el.scrollHeight
  return animate(el, {
    height: [0, targetHeight],
    opacity: [0, 1],
    duration,
    ease: 'outQuint',
    onComplete: () => {
      el.style.height = 'auto'
      el.style.overflow = ''
    },
  })
}

function slideUp(el: HTMLElement, duration = 500) {
  el.style.overflow = 'hidden'
  el.style.height = el.scrollHeight + 'px'
  return animate(el, {
    height: [el.scrollHeight, 0],
    opacity: [1, 0],
    duration,
    ease: 'inOutQuint',
    onComplete: () => {
      el.style.display = 'none'
      el.style.overflow = ''
    },
  })
}

function VisionContent() {
  return (
    <p className="font-heading text-xl md:text-2xl text-matou-teal-dark leading-relaxed max-w-3xl">
      {vision}
    </p>
  )
}

function MissionContent() {
  return (
    <p className="font-heading text-xl text-matou-teal-dark leading-relaxed max-w-3xl">
      {mission}
    </p>
  )
}

function ThemeCard({ theme, isSelected, onClick }: { theme: typeof themes[number]; isSelected: boolean; onClick: () => void }) {
  const count = objectives.filter((o) => o.theme === theme.id).length
  return (
    <div
      onClick={onClick}
      className="rounded-lg text-left cursor-pointer hover:-translate-y-px transition-all duration-150"
      style={{
        backgroundColor: isSelected ? theme.colorDark : theme.colorLight,
        borderLeft: `4px solid ${theme.colorDark}`,
      }}
    >
      <div className="p-4">
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-xs font-semibold px-2 py-0.5 rounded shrink-0"
            style={{
              backgroundColor: isSelected ? '#fff' : theme.colorDark,
              color: isSelected ? theme.colorDark : '#fff',
            }}
          >
            {theme.id}
          </span>
          <h3
            className="font-heading font-semibold"
            style={{ color: isSelected ? '#fff' : undefined }}
          >
            {theme.name}
          </h3>
          <span
            className="font-mono text-xs ml-auto shrink-0"
            style={{ color: isSelected ? 'rgba(255,255,255,0.7)' : undefined }}
          >
            {count}
          </span>
        </div>
      </div>
    </div>
  )
}

function ObjectivesContent() {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const objGridRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  const selected = themes.find((t) => t.id === selectedTheme)
  const themeObjectives = selected
    ? objectives.filter((o) => o.theme === selected.id)
    : []

  const animateObjectives = useCallback((wasOpen: boolean) => {
    requestAnimationFrame(() => {
      const objEl = objGridRef.current
      if (objEl) {
        if (wasOpen) {
          const cards = objEl.querySelectorAll('[data-obj-card]')
          if (cards.length > 0) {
            animate(cards, {
              opacity: [0, 1],
              translateY: [-12, 0],
              duration: 400,
              ease: 'outQuint',
              delay: stagger(60),
            })
          }
        } else {
          slideDown(objEl, 600)
          requestAnimationFrame(() => {
            const cards = objEl.querySelectorAll('[data-obj-card]')
            if (cards.length > 0) {
              animate(cards, {
                opacity: [0, 1],
                translateY: [-12, 0],
                duration: 500,
                ease: 'outQuint',
                delay: stagger(70, { start: 100 }),
              })
            }
          })
        }
      }
    })
  }, [])

  const handleSelect = useCallback((themeId: string) => {
    const isDeselect = selectedTheme === themeId

    if (isDeselect) {
      const objEl = objGridRef.current
      if (objEl) {
        slideUp(objEl, 400)
      }
      setTimeout(() => setSelectedTheme(null), 420)
    } else {
      const wasOpen = selectedTheme !== null
      setSelectedTheme(themeId)
      setMobileMenuOpen(false)
      animateObjectives(wasOpen)
    }
  }, [selectedTheme, animateObjectives])

  const toggleMobileMenu = useCallback(() => {
    const next = !mobileMenuOpen
    setMobileMenuOpen(next)
    requestAnimationFrame(() => {
      const el = mobileMenuRef.current
      if (!el) return
      if (next) {
        slideDown(el, 400)
      } else {
        slideUp(el, 300)
      }
    })
  }, [mobileMenuOpen])

  return (
    <div className="max-w-4xl w-full">
      <div className="hidden md:block">
        <p className="font-mono text-xs text-matou-text-muted uppercase tracking-wide mb-3">
          Select a theme to explore
        </p>
        <div className="grid grid-cols-4 gap-3">
          {themes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              isSelected={selectedTheme === theme.id}
              onClick={() => handleSelect(theme.id)}
            />
          ))}
        </div>
      </div>

      <div className="md:hidden">
        <button
          onClick={toggleMobileMenu}
          className="w-full flex items-center justify-between rounded-lg px-4 py-3 text-left transition-colors"
          style={{ backgroundColor: selected ? selected.colorDark : '#619793' }}
        >
          <span className="font-heading font-semibold text-white">
            {selected ? selected.name : 'Select a theme'}
          </span>
          <svg
            className="w-5 h-5 text-white transition-transform duration-200"
            style={{ transform: mobileMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div ref={mobileMenuRef} style={{ display: 'none', opacity: 0, height: 0 }}>
          <div className="flex flex-col gap-2 pt-2">
            {themes.map((theme) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                isSelected={selectedTheme === theme.id}
                onClick={() => handleSelect(theme.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedTheme && selected && (
        <div
          ref={objGridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6"
          style={{ display: 'none' }}
        >
          <div className="col-span-full mb-1">
            <h3 className="font-heading font-semibold text-matou-teal-dark">
              {selected.subtitle}
            </h3>
            <p className="font-mono text-sm text-matou-text-muted mt-1">
              {selected.description}
            </p>
          </div>
          {themeObjectives.map((obj) => (
            <div
              key={obj.id}
              data-obj-card
              className="bg-white rounded-lg p-5 border-l-4 mt-2"
              style={{ borderColor: selected.colorDark, opacity: 0 }}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="font-mono text-xs font-semibold px-2 py-0.5 rounded"
                  style={{ backgroundColor: selected.colorDark, color: '#fff' }}
                >
                  {obj.id}
                </span>
                <span className="font-mono text-xs text-matou-text-muted">
                  {obj.timeframe}
                </span>
              </div>
              <p className="font-mono text-sm text-matou-text">{obj.description}</p>
              {obj.dependencies.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {obj.dependencies.map((dep) => (
                    <span key={dep} className="font-mono text-[10px] bg-matou-teal-soft/20 text-matou-teal-deep px-1.5 py-0.5 rounded">
                      {dep}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StrategiesContent() {
  const priorityOrder = { primary: 0, supporting: 1, future: 2 } as const
  const sorted = [...strategies].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  )

  return (
    <div className="space-y-4 max-w-3xl w-full">
      {sorted.map((s) => (
        <div key={s.id} className="bg-white rounded-lg p-4 border-2 border-matou-teal-soft/30">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-xs px-2 py-0.5 rounded uppercase bg-matou-teal-soft/30 text-matou-teal-dark">
              {s.priority}
            </span>
            <h3 className="font-heading font-semibold text-matou-teal-dark">
              {s.title}
            </h3>
          </div>
          <p className="font-mono text-sm italic text-matou-text-muted mb-3">
            {s.tagline}
          </p>
          {s.whyPrimary && (
            <p className="font-mono text-sm text-matou-text mb-3">
              {s.whyPrimary}
            </p>
          )}
          <ul className="space-y-1">
            {s.approach.map((a, i) => (
              <li key={i} className="font-mono text-sm text-matou-text-muted flex gap-2">
                <span className="text-matou-teal-medium shrink-0">-</span>
                {a}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function TacticsContent() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-5xl">
      {quarters.map((q) => {
        const qTactics = tactics.filter((t) => t.quarter === q)
        return (
          <div key={q}>
            <h3 className="font-heading font-semibold text-matou-teal-dark mb-3">{q}</h3>
            <div className="space-y-2">
              {qTactics.map((t) => {
                const strat = strategies.find((s) => s.id === t.strategyId)
                return (
                  <div key={t.id} className="bg-white rounded-lg p-3 border-2 border-matou-teal-soft/30">
                    <span className="font-mono text-[10px] text-matou-teal-deep bg-matou-teal-soft/30 px-1.5 py-0.5 rounded">
                      {strat?.title ?? t.strategyId}
                    </span>
                    <p className="font-mono text-sm text-matou-teal-dark mt-1.5">
                      {t.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function VMOSTFramework() {
  const [expandedLayer, setExpandedLayer] = useState<LayerId | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const prevExpanded = useRef<LayerId | null>(null)

  const handleClick = useCallback((id: LayerId) => {
    if (isAnimating) return
    setExpandedLayer((prev) => (prev === id ? null : id))
  }, [isAnimating])

  useEffect(() => {
    const prev = prevExpanded.current
    const next = expandedLayer
    prevExpanded.current = next

    if (prev === next) return
    setIsAnimating(true)

    const close = prev ? contentRefs.current[prev] : null
    const open = next ? contentRefs.current[next] : null

    if (close && open) {
      slideUp(close, 400)
      setTimeout(() => {
        slideDown(open, 600)
        setTimeout(() => setIsAnimating(false), 650)
      }, 420)
    } else if (close) {
      slideUp(close, 500)
      setTimeout(() => setIsAnimating(false), 520)
    } else if (open) {
      slideDown(open, 600)
      setTimeout(() => setIsAnimating(false), 650)
    } else {
      setIsAnimating(false)
    }
  }, [expandedLayer])

  return (
    <div id="strategy" className="mt-12 md:mt-16" style={{ scrollMarginTop: '5rem' }}>
      <div className="max-w-4xl mx-auto text-center mb-16">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-matou-teal-dark mb-4">
          Where We Are Going
        </h2>
        <p className="font-mono text-sm md:text-base text-matou-text-muted leading-relaxed">
          Our strategy is built on five layers — from the vision that guides us, through the mission that grounds our work, objectives that hold us accountable, strategies that shape our approach, and tactics that move us forward. Select any layer to see what sits behind it.
        </p>
      </div>
      <div className="max-w-4xl mx-auto flex flex-col gap-5">
        {LAYERS.map((layer, index) => {
          const widthPercent = 40 + index * 15

          return (
            <div key={layer.id}>
              <button
                onClick={() => handleClick(layer.id)}
                className="mx-auto block rounded-lg cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-transform duration-300 relative"
                style={{
                  width: `${widthPercent}%`,
                  backgroundColor: layer.color,
                  border: layer.id === 'tactics' ? '2px solid #9AC7BF' : 'none',
                }}
              >
                <div className="flex items-center justify-center py-5 md:py-6">
                  <span
                    className="font-mono text-sm md:text-base font-medium uppercase tracking-widest"
                    style={{ color: layer.textColor }}
                  >
                    {layer.label}
                  </span>
                </div>
              </button>

              <div
                ref={(el) => { contentRefs.current[layer.id] = el }}
                style={{ display: 'none', opacity: 0, height: 0 }}
              >
                <div className="flex justify-center py-8 md:py-12 px-4 md:px-8 bg-matou-bg rounded-lg mt-2">
                  {layer.id === 'objectives'
                    ? <ObjectivesContent />
                    : (() => { const C = { vision: VisionContent, mission: MissionContent, strategies: StrategiesContent, tactics: TacticsContent }[layer.id]; return C ? <C /> : null })()
                  }
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="text-center mt-12">
        <a
          href="https://roadmap.matou.nz/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-8 py-3 border-2 border-matou-teal-dark text-matou-teal-dark font-mono text-sm font-semibold rounded-lg hover:bg-matou-teal-dark hover:text-white transition-colors"
        >
          For more detailed information, check out the Mātou Roadmap
        </a>
      </div>
    </div>
  )
}
